import React, { useState, useEffect, useRef } from 'react';
import { createNews, updateNews, listNews } from '../../services/cms';
import { uploadFileAndGetUrl } from '../../services/coa';
import RichTextEditor from '../ui/RichTextEditor';

const escapeHtml = (text) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Older articles were saved as plain text; convert them to paragraphs for the editor.
const toEditorHtml = (content) => {
  if (!content) return '';
  if (/<\/?[a-z][\s\S]*?>/i.test(content)) return content;
  return content
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const hasText = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return div.textContent.trim().length > 0;
};

const NewsModal = ({ isOpen, onClose, news }) => {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Latest Updates',
    featuredImage: '',
    isFeatured: false,
    status: 'draft',
    publishedAt: '',
    eventDetails: {
      date: '',
      location: '',
      hostedBy: ''
    },
    links: [{ label: '', url: '' }],
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [],
    relatedNews: []
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableNews, setAvailableNews] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const modalRef = useRef(null);

  const categories = [
    'Latest Updates',
    'Product Updates',
    'About COA Library',
    'Events & Webinars',
    'Insights & Research'
  ];

  useEffect(() => {
    if (isOpen) {
      if (news) {
        // Editing existing news
        setFormData({
          title: news.title || '',
          excerpt: news.excerpt || '',
          content: toEditorHtml(news.content),
          category: news.category || 'Latest Updates',
          featuredImage: news.featuredImage || '',
          isFeatured: news.isFeatured || false,
          status: news.status || 'draft',
          publishedAt: news.publishedAt ? new Date(news.publishedAt).toISOString().slice(0, 16) : '',
          eventDetails: {
            date: news.eventDetails?.date ? new Date(news.eventDetails.date).toISOString().slice(0, 16) : '',
            location: news.eventDetails?.location || '',
            hostedBy: news.eventDetails?.hostedBy || ''
          },
          links: news.links && news.links.length > 0 ? news.links : [{ label: '', url: '' }],
          metaTitle: news.metaTitle || '',
          metaDescription: news.metaDescription || '',
          metaKeywords: news.metaKeywords || [],
          relatedNews: news.relatedNews?.map(n => n._id || n) || []
        });
        if (news.featuredImage) {
          setImagePreview(news.featuredImage);
        }
      } else {
        // Creating new news
        setFormData({
          title: '',
          excerpt: '',
          content: '',
          category: 'Latest Updates',
          featuredImage: '',
          isFeatured: false,
          status: 'draft',
          publishedAt: '',
          eventDetails: { date: '', location: '', hostedBy: '' },
          links: [{ label: '', url: '' }],
          metaTitle: '',
          metaDescription: '',
          metaKeywords: [],
          relatedNews: []
        });
        setImagePreview(null);
        setImageFile(null);
      }
      loadAvailableNews();
    }
  }, [isOpen, news]);

  const loadAvailableNews = async () => {
    try {
      const response = await listNews({ limit: 100, status: 'published' });
      if (response && response.success !== false && response.data) {
        setAvailableNews(response.data.filter(n => !news || n._id !== news._id));
      }
    } catch (error) {
      console.error('Failed to load available news:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB');
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Store file for upload
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    setFormData(prev => ({ ...prev, featuredImage: '' }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('eventDetails.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        eventDetails: {
          ...prev.eventDetails,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleLinkChange = (index, field, value) => {
    const newLinks = [...formData.links];
    newLinks[index][field] = value;
    setFormData(prev => ({ ...prev, links: newLinks }));
  };

  const addLink = () => {
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, { label: '', url: '' }]
    }));
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasText(formData.content)) {
      alert('Please enter the article content.');
      return;
    }
    setLoading(true);

    try {
      // Upload image if a new file was selected
      let featuredImageUrl = formData.featuredImage; // Use existing image URL if no new file
      if (imageFile) {
        try {
          setUploadingImage(true);
          featuredImageUrl = await uploadFileAndGetUrl(imageFile);
          setUploadingImage(false);
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Failed to upload image. Please try again.');
          setUploadingImage(false);
          setLoading(false);
          return;
        }
      }

      // Build payload, filtering out empty values
      const payload = {
        title: formData.title,
        content: formData.content,
        category: formData.category,
        status: formData.status,
        isFeatured: formData.isFeatured,
        featuredImage: featuredImageUrl || undefined,
        ...(formData.excerpt && { excerpt: formData.excerpt }),
        ...(formData.publishedAt && { publishedAt: formData.publishedAt }),
        ...(formData.metaTitle && { metaTitle: formData.metaTitle }),
        ...(formData.metaDescription && { metaDescription: formData.metaDescription }),
        ...(formData.metaKeywords && formData.metaKeywords.length > 0 && { metaKeywords: formData.metaKeywords }),
        ...(formData.links && formData.links.filter(link => link.label && link.url).length > 0 && {
          links: formData.links.filter(link => link.label && link.url)
        }),
        ...(formData.relatedNews && formData.relatedNews.length > 0 && { relatedNews: formData.relatedNews }),
        ...(formData.eventDetails && (formData.eventDetails.date || formData.eventDetails.location || formData.eventDetails.hostedBy) && {
          eventDetails: {
            ...(formData.eventDetails.date && { date: formData.eventDetails.date }),
            ...(formData.eventDetails.location && { location: formData.eventDetails.location }),
            ...(formData.eventDetails.hostedBy && { hostedBy: formData.eventDetails.hostedBy })
          }
        })
      };

      const response = news
        ? await updateNews(news._id, payload)
        : await createNews(payload);

      if (response && response.success !== false) {
        alert(`News article ${news ? 'updated' : 'created'} successfully`);
        onClose();
      } else {
        alert(response?.error || `Failed to ${news ? 'update' : 'create'} news article`);
      }
    } catch (error) {
      console.error('Error saving news:', error);
      alert(`Failed to ${news ? 'update' : 'create'} news article`);
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {news ? 'Edit News Article' : 'Create News Article'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt (Max 500 characters)
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <div className="text-xs text-gray-500 mt-1">
                {formData.excerpt.length}/500 characters
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
                placeholder="Write the article..."
                contentMinHeightClass="min-h-[300px]"
                contentWhiteSpace="normal"
                paragraphMode
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image
              </label>
              <div className="space-y-3">
                {imagePreview && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden border border-gray-300">
                    <img 
                      src={imagePreview} 
                      alt="Featured" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-400 bg-white border-2 border-emerald-400 rounded-lg hover:bg-emerald-50 transition-colors">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12.75 6L9 2.25L5.25 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 2.25V11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {imagePreview ? 'Change Image' : 'Upload Image'}
                    </span>
                  </label>
                  {uploadingImage && (
                    <span className="text-sm text-gray-500">Uploading...</span>
                  )}
                </div>
                <p className="text-xs text-gray-500">We support PNGs, JPEGs and GIFs under 10MB</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                  className="w-4 h-4 text-emerald-400 border-gray-300 rounded focus:ring-emerald-400"
                />
                <span className="text-sm font-medium text-gray-700">Featured Article</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Published Date
                </label>
                <input
                  type="datetime-local"
                  name="publishedAt"
                  value={formData.publishedAt}
                  onChange={handleChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
            </div>
          </div>

          {/* Event Details (for Events & Webinars) */}
          {formData.category === 'Events & Webinars' && (
            <div className="space-y-4 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900">Event Details</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date
                  </label>
                  <input
                    type="datetime-local"
                    name="eventDetails.date"
                    value={formData.eventDetails.date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="eventDetails.location"
                    value={formData.eventDetails.location}
                    onChange={handleChange}
                    placeholder="University of Birmingham"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hosted By
                  </label>
                  <input
                    type="text"
                    name="eventDetails.hostedBy"
                    value={formData.eventDetails.hostedBy}
                    onChange={handleChange}
                    placeholder="CPROR"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Links */}
          <div className="space-y-4 border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Links</h3>
              <button
                type="button"
                onClick={addLink}
                className="text-sm text-emerald-400 hover:text-emerald-500 font-medium"
              >
                + Add Link
              </button>
            </div>
            
            {formData.links.map((link, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  placeholder="Label"
                  value={link.label}
                  onChange={(e) => handleLinkChange(index, 'label', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <input
                  type="url"
                  placeholder="URL"
                  value={link.url}
                  onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                {formData.links.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLink(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* SEO */}
          <div className="space-y-4 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900">SEO Settings</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title
              </label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description
              </label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
          </div>

          {/* Related News */}
          <div className="space-y-4 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900">Related News</h3>
            <select
              multiple
              value={formData.relatedNews}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                setFormData(prev => ({ ...prev, relatedNews: selected }));
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              size={5}
            >
              {availableNews.map(item => (
                <option key={item._id} value={item._id}>
                  {item.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="px-4 py-2 bg-emerald-400 text-white rounded-lg hover:bg-emerald-500 font-medium disabled:opacity-50"
            >
              {loading || uploadingImage ? 'Saving...' : (news ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsModal;

